import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import {
  ConsentTypes,
  FLOW_TYPE,
} from '@credit-acceptance/shareable-utilities';
import { FullStory } from '@fullstory/browser';
import { adobeAnalytics, TFormSubmissionStatus } from 'adobe-analytics';
import {
  catchError,
  EMPTY,
  map,
  NEVER,
  of,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';

import { PREQUAL_JSON } from '../../assets/constants';
import { SSNError, SSNErrorType } from '../interfaces/common';
import { ConsentTextInfoRestService } from '../services/consent-test-info-rest.service';
import {
  ReSubmitLongFormApplicationResponse,
  SubmitLongFormApplicationResponse,
} from '../services/prequal-rest-responses-interface';
import { PrequalService } from '../services/prequal.service';
import {
  RecaptchaAction,
  RecaptchaService,
} from '../services/recaptcha.service';
import { RuntimeConfigService } from '../services/runtime-config';
import { SkipRecaptchaService } from '../services/skip-recaptcha.service';
import { LongFormApplicationRestService } from '../services/submit-long-form-rest.service';
import { Unsubscribe } from '../shared/classes/unsubscribe.class';
import { FS_EVENTS, FS_LABELS } from '../shared/constants/fsevents';
import { DealerLocation } from '../shared/models/prequal.types';
import {
  accessToken,
  cacChubSSNId,
  monthlyPayment,
  portalApplicationId,
} from '../shared/state/local-cache';
import {
  SiblingValidationService,
  ValidationResult,
} from '../validators/sibling-validation.service';
import {
  isCapsValidationFailure,
  scrollToServerField,
  ServerFieldError,
} from './caps-validation-field-errors';
import { componentMapping, payloadCompiler } from './helpers';

declare const FS: typeof FullStory;

@Component({
  selector: 'capq-summary',
  templateUrl: './summary.component.html',
  styleUrls: ['./summary.component.scss'],
  standalone: false,
})
export class SummaryComponent extends Unsubscribe implements OnInit {
  @Output() activeStep = new EventEmitter<number>();
  @Output() setConsent = new EventEmitter<boolean>();
  @Output() resetIdentificationConsent = new EventEmitter<void>();
  @Output() ssnError: EventEmitter<SSNError> = new EventEmitter<SSNError>();
  @Output() submissionFailureReason: EventEmitter<string> =
    new EventEmitter<string>();
  @Input() adobeScriptLoaded = false;
  @Input() allConsentsGiven!: boolean;
  @Input() userDealerIds: string[] = [];
  @Input() smsConsentGiven = false;

  emailDisclosure = ConsentTypes.OSM_EMAIL_PHONE;
  NO_ACCOUNT = 'No Account';
  globalText: any;

  summaryConsentAgreed = new FormControl(false);
  personal_labels: any;
  personalData: any;
  residence_labels: any;
  residenceData: any;
  income_labels: any;
  primaryIncomeData: any;
  secondaryIncomeData: any;
  banking_labels: any;
  bankingData: string[] | undefined;
  identification_labels: any;
  steps: any;
  emailPhoneConsent = '';
  emailPhoneConsentVersion = '';
  secondaryIncomeDisplay = '';
  primaryIncomeDisplay = '';
  incomeOptionsList: any;
  emailConsentTextId = 0;
  dealerLocators: DealerLocation[] | undefined;
  openLoanOrNoBureauError: boolean | undefined;
  errorMessage: any | undefined;
  errorType: string | undefined;
  isInvalidSsn: boolean | undefined;
  canResubmit = false;
  summary: any;
  failureReason = '';
  isWaiting = false;
  private sortedResults: ValidationResult[] = [];
  private alive = true;

  componentsOrder = {
    1: '#personal-content',
    2: '#residence-content',
  };

  // challengeSolved = inject(SkipRecaptchaService).skipRecaptcha();

  constructor(
    private readonly runtimeConfigService: RuntimeConfigService,
    private readonly siblingValidationService: SiblingValidationService,
    private readonly prequalService: PrequalService,
    private readonly skipRecaptchaService: SkipRecaptchaService,
    private readonly consentTextInfoRestService: ConsentTextInfoRestService,
    private readonly longFormApplicationRestService: LongFormApplicationRestService,
    private readonly recaptchaService: RecaptchaService
  ) {
    super();
  }

  ngOnInit(): void {
    this.consentTextInfoRestService
      .getConsentByType(ConsentTypes.OSM_EMAIL_PHONE)
      .pipe(
        tap((resp) => {
          const accessTokenRef = resp.headers?.get('access-token');
          if (accessTokenRef) accessToken(accessTokenRef);
          const chubSSNid = resp.headers?.get('cac-chub-ssid');
          if (chubSSNid) cacChubSSNId(chubSSNid);
        }),
        map((resp) => resp.body?.getConsentByType),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((emailPhoneConsent) => {
        if (emailPhoneConsent) {
          this.emailPhoneConsent = emailPhoneConsent.consenttext;
          this.emailPhoneConsentVersion = emailPhoneConsent.consentversion;
          this.emailConsentTextId = emailPhoneConsent.consenttextid;
        }
      });

    this.globalText = PREQUAL_JSON;
    this.summary = PREQUAL_JSON?.summary;
    this.personal_labels = PREQUAL_JSON?.personal;
    this.residence_labels = PREQUAL_JSON?.residence;
    this.income_labels = PREQUAL_JSON?.income;
    this.incomeOptionsList =
      PREQUAL_JSON?.income?.formsFields?.incomeType?.options;
    this.banking_labels = PREQUAL_JSON?.accounts;
    this.identification_labels = PREQUAL_JSON?.identification;

    this.summaryConsentAgreed.addValidators(Validators.pattern('true'));
    this.summaryConsentAgreed.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((value) => {
        this.adobeScriptLoaded && adobeAnalytics.emitFormStart('agreement');
        this.setConsent.emit(value ?? false);
      });

    this.siblingValidationService.validationResult$
      .pipe(
        switchMap((validationResults) => {
          const { validationPassed, sortedResults } =
            this.handleValidationResults(validationResults);

          if (validationPassed && !this.skipRecaptchaService.skipRecaptcha()) {
            return this.recaptchaService
              .execute(RecaptchaAction.SUBMIT_PREQUAL)
              .pipe(
                map((token) => ({
                  validationPassed,
                  token: token ?? undefined,
                  sortedResults,
                }))
              );
          }

          return of({ validationPassed, token: '', sortedResults });
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(({ validationPassed, sortedResults, token }) => {
        if (validationPassed) {
          this.handleSubmit(sortedResults, token);
        } else {
          this.isWaiting = false;
        }
      });
  }

  processFormSubmission() {
    this.isWaiting = true;
    this.siblingValidationService.triggerValidation();
  }

  handleSubmit(sortedResults: ValidationResult[], recaptchaToken = '') {
    if (!(typeof FS === 'undefined')) {
      FS.event(FS_EVENTS.PREQUALIFICATION, {
        action_label: FS_LABELS.PREQUALIFICATION,
        prequalificationSubmitted: true,
      });
    }

    const headers = this.skipRecaptchaService.skipRecaptchaDueToTestScript()
      ? {
          'Is-Test-Script': 'true',
        }
      : {
          recaptcha: recaptchaToken, // Set the recaptcha token as a header for submission to the backend.
        };

    if (portalApplicationId() > 0 && this.canResubmit) {
      this.canResubmit = false;
      this.isInvalidSsn = false;
      const inputData: any = payloadCompiler(
        sortedResults,
        this.summaryConsentAgreed.value!,
        undefined,
        this.smsConsentGiven
      );

      inputData.longFormInfoInput.vehicleType =
        this.prequalService.getVehicleType();
      inputData.longFormInfoInput.vehicleMake =
        this.prequalService.getVehicleMake();

      // Modern browser sessionStorage check and fallback for flowType
      let flowType = FLOW_TYPE.OSM.toString();
      try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
          const flowTypeValue = sessionStorage.getItem('prequalFlowType');
          if (flowTypeValue != null) {
            const parsed = JSON.parse(flowTypeValue);
            if (parsed && parsed.flowType) {
              flowType = parsed.flowType;
            }
          }
        }
      } catch (e) {
        // sessionStorage not available or error occurred, fallback to default
      }
      inputData.longFormInfoInput.personal.application.flowtype = flowType;

      this.longFormApplicationRestService
        .reSubmitLongFormApplication(inputData.longFormInfoInput, headers)
        .pipe(
          tap((response) => {
            this.processReSubmitDataForRest(response, inputData);
          }),
          catchError((context) => {
            if (!(typeof FS === 'undefined')) {
              FS.setUserVars({
                applicationId: context?.error?.applicationId ?? 0,
                FormType:
                  inputData.longFormInfoInput.personal.application.flowtype,
              });
            }
            this.emitServerErrorFsEvent(context?.error?.applicationId ?? 0);
            console.error(`Prequal submission failed due to: ${context}`);
            this.prequalService.triggerErrorScreen(true);
            return EMPTY;
          }),
          takeUntil(this.unsubscribe$)
        )
        .subscribe();
    } else {
      const inputData: any = payloadCompiler(
        sortedResults,
        this.summaryConsentAgreed.value!,
        this.userDealerIds,
        this.smsConsentGiven
      );

      inputData.longFormInfoInput.vehicleType =
        this.prequalService.getVehicleType();
      inputData.longFormInfoInput.vehicleMake =
        this.prequalService.getVehicleMake();

      // Modern browser sessionStorage check and fallback for flowType
      let flowType = FLOW_TYPE.OSM.toString();
      try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
          const flowTypeValue = sessionStorage.getItem('prequalFlowType');
          if (flowTypeValue != null) {
            const parsed = JSON.parse(flowTypeValue);
            if (parsed && parsed.flowType) {
              flowType = parsed.flowType;
            }
          }
        }
      } catch (e) {
        // sessionStorage not available or error occurred, fallback to default
      }
      inputData.longFormInfoInput.personal.application.flowtype = flowType;

      this.longFormApplicationRestService
        .submitLongFormApplication(inputData.longFormInfoInput, headers)
        .pipe(
          tap((response) => {
            this.processSubmitDataForRest(response, inputData);
          }),
          catchError((context) => {
            if (!(typeof FS === 'undefined')) {
              FS.setUserVars({
                applicationId: context?.error?.applicationId ?? 0,
                FormType:
                  inputData.longFormInfoInput.personal.application.flowtype,
              });
            }
            this.emitServerErrorFsEvent(context?.error?.applicationId ?? 0);
            console.error(`Prequal submission failed due to: ${context}`);
            this.prequalService.triggerErrorScreen(true);
            return EMPTY;
          }),
          takeUntil(this.unsubscribe$)
        )
        .subscribe();
    }
  }

  private emitInvalidResponseFsEvent(status: string) {
    if (!(typeof FS === 'undefined')) {
      FS.event(FS_EVENTS.PREQUAL_INVALID_RESPONSE, {
        action_label: FS_LABELS.INVALID_RESPONSE,
        invalidResponse: true,
        applicationId: portalApplicationId() ?? 0,
        status,
      });
    }
  }

  private emitServerErrorFsEvent(applicationId: number) {
    if (!(typeof FS === 'undefined')) {
      FS.event(FS_EVENTS.PREQUAL_SERVER_ERROR, {
        action_label: FS_LABELS.SERVER_ERROR,
        serverError: true,
        applicationId,
      });
    }
  }

  private processSubmitDataForRest(
    data: SubmitLongFormApplicationResponse,
    inputData: any
  ) {
    this.isWaiting = false;
    let status = '';
    if (data) {
      if (data?.submitLongFormApplication?.dealerLocations) {
        this.prequalService.setDealerLocations(
          data.submitLongFormApplication.dealerLocations as DealerLocation[]
        );
      }
      this.prequalService.setOpenLoan(
        !!data?.submitLongFormApplication?.openLoan
      );
      status = data?.submitLongFormApplication?.status;
      data?.submitLongFormApplication?.portalApplicationId &&
        portalApplicationId(
          data?.submitLongFormApplication?.portalApplicationId
        );
      if (!(typeof FS === 'undefined')) {
        FS.setUserVars({
          applicationId:
            data?.submitLongFormApplication?.portalApplicationId ?? 0,
          FormType: inputData.longFormInfoInput.personal.application.flowtype,
        });
      }

      if (status === 'success') {
        data?.submitLongFormApplication?.maxMonthlyPayment &&
          monthlyPayment(data?.submitLongFormApplication?.maxMonthlyPayment);
        this.activeStep.emit(7);
      } else if (status === 'failure') {
        this.failureReason = data.submitLongFormApplication?.failureReason;
        // SVRS-56614: takes precedence over every other failure branch below —
        // no prequal decision, offer, or lead is shown when either flag is true.
        if (
          data.submitLongFormApplication?.primaryApplicant?.bureauFlags
            ?.fraudFlag ||
          data.submitLongFormApplication?.primaryApplicant?.bureauFlags
            ?.deceasedSsnFlag
        ) {
          this.setVerificationRequiredData();
        } else if (data.submitLongFormApplication?.invalidSsn) {
          if (
            data.submitLongFormApplication?.failureReason.includes('blocked')
          ) {
            this.errorMessage = this.summary.errors.sme2;
            this.errorType = 'blockedSSN';
            this.ssnError.emit({
              type: SSNErrorType.BLOCKED_SSN,
              message: this.errorMessage,
            });
            this.clearAllInputFields();
          } else {
            this.errorMessage = this.summary.errors.sme1;
            this.errorType = 'invalidSSN';
            this.ssnError.emit({
              type: SSNErrorType.INVALID_SSN,
              message: this.errorMessage,
            });
            this.prequalService.setErrorMessage(true);
            this.isInvalidSsn = true;
            this.canResubmit = true;
          }
          this.uncheckAgreementCheckboxes();
          document.getElementById('error-subsection')?.scrollIntoView();
        } else if (data.submitLongFormApplication?.openLoan) {
          if (!(typeof FS === 'undefined')) {
            FS.setUserVars({
              action_label: FS_LABELS.OPEN_LOAN,
              openLoanExist: true,
            });
          }

          this.setSpecialErrorScenarioData();
        } else if (isCapsValidationFailure(data.submitLongFormApplication)) {
          this.applyCapsValidationErrors(
            data.submitLongFormApplication.fieldErrors as ServerFieldError[]
          );
        } else {
          this.emitInvalidResponseFsEvent(status);
          this.prequalService.triggerErrorScreen(true);
        }
      } else {
        this.emitInvalidResponseFsEvent(status);
        this.prequalService.triggerErrorScreen(true);
      }
    }

    if (!(typeof FS === 'undefined')) {
      FS.event(FS_EVENTS.PREQUALIFICATION, {
        action_label: FS_LABELS.PREQUALIFICATION,
        prequalificationSubmitted: true,
      });
    }

    this.adobeScriptLoaded &&
      adobeAnalytics.emitEvent('cac-form-complete', null, {
        form_design: 'v 1.0',
        section_name: 'summary',
        form_submission_id: (portalApplicationId() ?? '').toString(),
        dealer_list: '',
        form_submission_status: status as TFormSubmissionStatus,
        event_type: 'cac-form-complete',
        max_finance_amount: data?.submitLongFormApplication?.maxFinanceAmount!,
        max_monthly_payment_amount:
          data?.submitLongFormApplication?.maxMonthlyPayment!,
        min_down_payment_amount:
          data?.submitLongFormApplication?.minimumDownPayment!,
        test_experience: 'form prequal monthlypayment challenger1',
        test_name: 'form prequal monthlypayment ',
        test_response: 'monthly_payment',
        form_failure_reason: this.failureReason,
        email_address: inputData?.longFormInfoInput?.personal?.email,
        phone_number: inputData.longFormInfoInput?.personal.phone,
        user_zip: inputData.longFormInfoInput?.residence?.zipCode,
        user_city: inputData.longFormInfoInput?.residence?.city,
        user_state: inputData.longFormInfoInput?.residence?.userState,
        consumer_uuid: cacChubSSNId(),
        vehicle_type: inputData?.longFormInfoInput?.vehicleType,
        vehicle_make: inputData?.longFormInfoInput?.vehicleMake,
      });
  }

  private processReSubmitDataForRest(
    data: null | undefined | ReSubmitLongFormApplicationResponse,
    inputData: any
  ) {
    this.isWaiting = false;
    let status = '';
    if (data) {
      if (data?.reSubmitLongFormApplication?.dealerLocations) {
        this.prequalService.setDealerLocations(
          data.reSubmitLongFormApplication.dealerLocations as DealerLocation[]
        );
      }
      this.prequalService.setOpenLoan(
        !!data?.reSubmitLongFormApplication?.openLoan
      );
      status = data?.reSubmitLongFormApplication?.status;
      data?.reSubmitLongFormApplication?.portalApplicationId &&
        portalApplicationId(
          data?.reSubmitLongFormApplication?.portalApplicationId
        );
      if (!(typeof FS === 'undefined')) {
        FS.setUserVars({
          applicationId:
            data?.reSubmitLongFormApplication?.portalApplicationId ?? 0,
          FormType: inputData.longFormInfoInput.personal.application.flowtype,
        });
      }

      if (status === 'success') {
        data?.reSubmitLongFormApplication?.maxMonthlyPayment &&
          monthlyPayment(data?.reSubmitLongFormApplication?.maxMonthlyPayment);
        this.activeStep.emit(7);
      } else if (status === 'failure') {
        this.failureReason = data.reSubmitLongFormApplication?.failureReason;
        // SVRS-56614: takes precedence over every other failure branch below —
        // no prequal decision, offer, or lead is shown when either flag is true.
        if (
          data.reSubmitLongFormApplication?.primaryApplicant?.bureauFlags
            ?.fraudFlag ||
          data.reSubmitLongFormApplication?.primaryApplicant?.bureauFlags
            ?.deceasedSsnFlag
        ) {
          this.setVerificationRequiredData();
        } else if (data.reSubmitLongFormApplication?.invalidSsn) {
          if (
            data.reSubmitLongFormApplication?.failureReason.includes('blocked')
          ) {
            this.errorMessage = this.summary.errors.sme2;
            this.errorType = 'blockedSSN';
            this.ssnError.emit({
              type: SSNErrorType.BLOCKED_SSN,
              message: this.errorMessage,
            });
            this.clearAllInputFields();
          } else {
            this.errorMessage = this.summary.errors.sme1;
            this.errorType = 'invalidSSN';
            this.ssnError.emit({
              type: SSNErrorType.INVALID_SSN,
              message: this.errorMessage,
            });
            this.prequalService.setErrorMessage(true);
            this.isInvalidSsn = true;
            this.canResubmit = true;
          }
          this.uncheckAgreementCheckboxes();
          document.getElementById('error-subsection')?.scrollIntoView();
        } else if (data.reSubmitLongFormApplication?.openLoan) {
          if (!(typeof FS === 'undefined')) {
            FS.setUserVars({
              action_label: FS_LABELS.OPEN_LOAN,
              openLoanExist: true,
            });
          }

          this.setSpecialErrorScenarioData();
        } else if (isCapsValidationFailure(data.reSubmitLongFormApplication)) {
          this.applyCapsValidationErrors(
            data.reSubmitLongFormApplication.fieldErrors as ServerFieldError[]
          );
        } else {
          this.emitInvalidResponseFsEvent(status);
          this.prequalService.triggerErrorScreen(true);
        }
      } else {
        this.emitInvalidResponseFsEvent(status);
        this.prequalService.triggerErrorScreen(true);
      }
    }

    if (!(typeof FS === 'undefined')) {
      FS.event(FS_EVENTS.PREQUALIFICATION, {
        action_label: FS_LABELS.PREQUALIFICATION,
        prequalificationSubmitted: true,
      });
    }

    this.adobeScriptLoaded &&
      adobeAnalytics.emitEvent('cac-form-complete', null, {
        form_design: 'v 1.0',
        section_name: 'summary',
        form_submission_id: (portalApplicationId() ?? '').toString(),
        dealer_list: '',
        form_submission_status: status as TFormSubmissionStatus,
        event_type: 'cac-form-complete',
        max_finance_amount:
          data?.reSubmitLongFormApplication?.maxFinanceAmount!,
        max_monthly_payment_amount:
          data?.reSubmitLongFormApplication?.maxMonthlyPayment!,
        min_down_payment_amount:
          data?.reSubmitLongFormApplication?.minimumDownPayment!,
        test_experience: 'form prequal monthlypayment challenger1',
        test_name: 'form prequal monthlypayment ',
        test_response: 'monthly_payment',
        form_failure_reason: this.failureReason,
        email_address: inputData?.longFormInfoInput?.personal?.email,
        phone_number: inputData.longFormInfoInput?.personal.phone,
        user_zip: inputData.longFormInfoInput?.residence?.zipCode,
        user_city: inputData.longFormInfoInput?.residence?.city,
        user_state: inputData.longFormInfoInput?.residence?.userState,
        consumer_uuid: cacChubSSNId(),
        vehicle_type: inputData?.longFormInfoInput?.vehicleType,
        vehicle_make: inputData?.longFormInfoInput?.vehicleMake,
      });
  }

  private applyCapsValidationErrors(fieldErrors: ServerFieldError[]) {
    this.canResubmit = true;
    this.uncheckAgreementCheckboxes();
    this.prequalService.setServerFieldErrors(fieldErrors);
    scrollToServerField(fieldErrors[0].field);
  }

  handleValidationResults(results: ValidationResult[]): {
    validationPassed: boolean;
    sortedResults: ValidationResult[];
  } {
    let validationPassed = true;
    const sortedResults = [...results].sort(
      (a: ValidationResult, b: ValidationResult) =>
        a.componentId - b.componentId
    );
    const firstInvalidComponent = sortedResults.find(
      (result: any) => !result.isValid
    );
    if (firstInvalidComponent) {
      validationPassed = false;
      const id =
        componentMapping[
          firstInvalidComponent.componentId as keyof typeof componentMapping
        ];
      if (firstInvalidComponent && id) {
        document
          .querySelector(id)
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    return {
      validationPassed,
      sortedResults,
    };
  }

  setSpecialErrorScenarioData() {
    this.openLoanOrNoBureauError = true;
    this.activeStep.emit(8);
  }

  /** SVRS-56614: routes to the "Additional Verification Required" result screen. */
  setVerificationRequiredData() {
    this.activeStep.emit(9);
  }

  clearAllInputFields() {
    (document.getElementById('cac-summary-submit') as any).disabled = true;

    document.querySelectorAll('input').forEach((input) => {
      input.value = '';
    });

    document.querySelectorAll('input[type=checkbox]').forEach((input) => {
      (input as any).disabled = true;
      (input as any).checked = false;
    });
  }

  uncheckAgreementCheckboxes() {
    this.summaryConsentAgreed.setValue(false);
    this.resetIdentificationConsent.emit();
  }
}
